import { TaskCreate } from "@common/contracts/task.contract";
import { isPlainObject } from "@common/helpers/typeGuards";
import { MessageType } from "@common/types/chat";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import {
    PersistableView,
    PersistableViewSchema,
} from "@common/types/views/views";
import {
    isReportTaskType,
    replaceReportDisplayFilters,
} from "@components/reports/reportDisplayFilters";
import { getTaskPreviewDefinition } from "./taskCreationPreviewRegistry";
import {
    getWorkflowViewForType,
    isWorkspaceWorkflowType,
} from "./taskWorkflow";

const resolveTemplateType = (template: OrganizationTaskTemplate): TaskType =>
    template.type ?? TaskType.QUESTION;

export const filterOrgTaskTemplatesForWorkspaceTask = (
    templates: readonly OrganizationTaskTemplate[],
    isTaskTypeAllowed: (type: TaskType) => boolean = () => true,
): OrganizationTaskTemplate[] =>
    templates.filter((template) => {
        const type = resolveTemplateType(template);
        return isWorkspaceWorkflowType(type) && isTaskTypeAllowed(type);
    });

interface ApplyOrgTaskTemplateParams {
    template: OrganizationTaskTemplate;
    values: TaskCreate;
    now: Date;
    assignableUserIds: readonly number[];
    availableGroupIds: readonly string[];
}

type TemplateWorkflow = Pick<TaskCreate, "view" | "sticky">;

const resolveTemplateViewWorkflow = ({
    template,
    createDefaultView,
    isTemplateSticky,
}: {
    template: OrganizationTaskTemplate;
    createDefaultView: () => PersistableView;
    isTemplateSticky: (viewConfig: object | null) => boolean;
}): TemplateWorkflow => {
    const parsedView = PersistableViewSchema.safeParse(template.viewConfig);
    if (!parsedView.success) {
        return { view: createDefaultView(), sticky: undefined };
    }
    return {
        view: parsedView.data,
        sticky: isTemplateSticky(
            isPlainObject(template.viewConfig) ? template.viewConfig : null,
        ),
    };
};

const resolveTemplateWorkflow = ({
    template,
    type,
    values,
    now,
}: Pick<ApplyOrgTaskTemplateParams, "template" | "values" | "now"> & {
    type: TaskType;
}): TemplateWorkflow => {
    const previewDefinition = getTaskPreviewDefinition(type);
    if (
        previewDefinition?.createDefaultView &&
        previewDefinition.isTemplateSticky
    ) {
        return resolveTemplateViewWorkflow({
            template,
            createDefaultView: previewDefinition.createDefaultView,
            isTemplateSticky: previewDefinition.isTemplateSticky,
        });
    }
    const view = getWorkflowViewForType({
        type,
        currentView: values.view,
        now,
    });
    if (!view || !isReportTaskType(type)) {
        return { view, sticky: undefined };
    }
    const parsedTemplateView = PersistableViewSchema.safeParse(
        template.viewConfig,
    );
    return {
        view: {
            ...view,
            filters: replaceReportDisplayFilters({
                taskType: type,
                filters: view.filters,
                displayFilters: parsedTemplateView.success
                    ? parsedTemplateView.data.filters
                    : undefined,
            }),
        },
        sticky: undefined,
    };
};

export const applyOrgTaskTemplateToTaskValues = ({
    template,
    values,
    now,
    assignableUserIds,
    availableGroupIds,
}: ApplyOrgTaskTemplateParams): TaskCreate => {
    const type = resolveTemplateType(template);
    const isAssigneeAvailable =
        template.assigneeId !== null &&
        assignableUserIds.includes(template.assigneeId);
    const isGroupAvailable =
        template.groupId !== null &&
        availableGroupIds.includes(template.groupId);

    return {
        ...values,
        title: template.title,
        type,
        ...resolveTemplateWorkflow({ template, type, values, now }),
        ...(template.initialMessage === null
            ? {}
            : {
                  description: template.initialMessage,
                  descriptionType: MessageType.SLATE,
              }),
        priority: template.priority ?? values.priority,
        estimatedTime: template.estimatedTime ?? values.estimatedTime,
        assigneeId: isAssigneeAvailable
            ? template.assigneeId
            : values.assigneeId,
        groupId: isGroupAvailable ? template.groupId : values.groupId,
        closeStageId: values.closeId
            ? (template.closeStageId ?? values.closeStageId)
            : values.closeStageId,
    };
};
